import React, { useEffect, useState } from 'react'
import { MessageOutlined } from '@ant-design/icons'
import {
	Button,
	Card,
	Col,
	Collapse,
	Divider,
	Input,
	Radio,
	Row,
	Space,
	Spin,
	Typography,
	message,
} from 'antd'
import { httpGet, httpPut } from '../../common/http.service'
import { CHECKIN_REASON_ROWS, CHECKOUT_REASON_ROWS } from './messageTypesReasons'

const { Title, Text, Paragraph } = Typography

type MessageStyle = 'long' | 'short'

const DEFAULT_SHORT = {
	checkInSuccess: '✅ Check-in successful.',
	checkOutSuccess: '✅ Check-out successful.',
	checkInError:
		'Unable to check in. Please try again or contact your supervisor.',
	checkOutError:
		'Unable to check out. Please try again or contact your supervisor.',
}

function buildReasonMap(
	rows: { code: string }[],
	source: Record<string, string> | undefined,
): Record<string, string> {
	const m: Record<string, string> = {}
	for (const { code } of rows) {
		m[code] = (source?.[code] ?? '').trim()
	}
	return m
}

function packReasonOverrides(
	rows: { code: string }[],
	state: Record<string, string>,
): Record<string, string> {
	const out: Record<string, string> = {}
	for (const { code } of rows) {
		const v = (state[code] ?? '').trim()
		if (v.length > 0) out[code] = v
	}
	return out
}

const MessageTypesTab: React.FC = () => {
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [style, setStyle] = useState<MessageStyle>('long')
	const [shortInOk, setShortInOk] = useState(DEFAULT_SHORT.checkInSuccess)
	const [shortOutOk, setShortOutOk] = useState(DEFAULT_SHORT.checkOutSuccess)
	const [shortInErr, setShortInErr] = useState(DEFAULT_SHORT.checkInError)
	const [shortOutErr, setShortOutErr] = useState(DEFAULT_SHORT.checkOutError)
	const [checkInReasons, setCheckInReasons] = useState<Record<string, string>>({})
	const [checkOutReasons, setCheckOutReasons] = useState<Record<string, string>>({})

	const load = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/app-settings')
			const d = response?.data ?? {}
			const s = (d.checkInCheckoutUserMessageStyle as MessageStyle) || 'long'
			setStyle(s === 'short' ? 'short' : 'long')
			const m = d.shortCheckInCheckoutMessages ?? {}
			setShortInOk(m.checkInSuccess ?? DEFAULT_SHORT.checkInSuccess)
			setShortOutOk(m.checkOutSuccess ?? DEFAULT_SHORT.checkOutSuccess)
			setShortInErr(m.checkInError ?? DEFAULT_SHORT.checkInError)
			setShortOutErr(m.checkOutError ?? DEFAULT_SHORT.checkOutError)
			setCheckInReasons(
				buildReasonMap(CHECKIN_REASON_ROWS, d.checkInShortReasonMessages),
			)
			setCheckOutReasons(
				buildReasonMap(CHECKOUT_REASON_ROWS, d.checkOutShortReasonMessages),
			)
		} catch (e) {
			console.error(e)
			message.error('Failed to load message settings')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		load()
	}, [])

	const validateShort = (): boolean => {
		const max = 500
		const fields = [
			{ v: shortInOk, name: 'Check-in success (short)' },
			{ v: shortOutOk, name: 'Check-out success (short)' },
			{ v: shortInErr, name: 'Check-in fallback error (short)' },
			{ v: shortOutErr, name: 'Check-out fallback error (short)' },
		]
		for (const { v, name } of fields) {
			if (typeof v !== 'string' || v.trim().length === 0) {
				message.error(`${name} cannot be empty when Short Message is selected`)
				return false
			}
			if (v.length > max) {
				message.error(`${name} must be at most ${max} characters`)
				return false
			}
		}
		for (const { code } of CHECKIN_REASON_ROWS) {
			const v = (checkInReasons[code] ?? '').trim()
			if (v.length > max) {
				message.error(`Check-in “${code}” must be at most ${max} characters`)
				return false
			}
		}
		for (const { code } of CHECKOUT_REASON_ROWS) {
			const v = (checkOutReasons[code] ?? '').trim()
			if (v.length > max) {
				message.error(`Check-out “${code}” must be at most ${max} characters`)
				return false
			}
		}
		return true
	}

	const handleSave = async () => {
		if (style === 'short' && !validateShort()) return
		try {
			setSaving(true)
			const response: any = await httpGet('/api/v1/admin/app-settings')
			const current = response?.data ?? {}
			const checkInShortReasonMessages =
				style === 'short'
					? packReasonOverrides(CHECKIN_REASON_ROWS, checkInReasons)
					: current.checkInShortReasonMessages
			const checkOutShortReasonMessages =
				style === 'short'
					? packReasonOverrides(CHECKOUT_REASON_ROWS, checkOutReasons)
					: current.checkOutShortReasonMessages

			await httpPut('/api/v1/admin/app-settings', {
				...current,
				checkInCheckoutUserMessageStyle: style,
				shortCheckInCheckoutMessages: {
					checkInSuccess: shortInOk.trim(),
					checkOutSuccess: shortOutOk.trim(),
					checkInError: shortInErr.trim(),
					checkOutError: shortOutErr.trim(),
				},
				...(style === 'short'
					? { checkInShortReasonMessages, checkOutShortReasonMessages }
					: {}),
			})
			message.success('Message type settings saved')
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to save')
		} finally {
			setSaving(false)
		}
	}

	const reasonField = (
		code: string,
		label: string,
		value: string,
		onChange: (v: string) => void,
	) => (
		<Col xs={24} key={code}>
			<Text type='secondary' style={{ fontSize: 11, display: 'block' }}>
				{code}
			</Text>
			<Text strong style={{ display: 'block', marginBottom: 4 }}>
				{label}
			</Text>
			<Input
				placeholder='Leave empty to use server default for this situation'
				value={value}
				maxLength={500}
				onChange={(e) => onChange(e.target.value)}
			/>
		</Col>
	)

	return (
		<div>
			<Spin spinning={loading}>
				<Card
					title={
						<span>
							<MessageOutlined style={{ marginRight: 8 }} />
							Message types (mobile check-in / check-out)
						</span>
					}
					bordered={false}
					style={{}}
				>
					<Paragraph type='secondary'>
						Applies only to <Text strong>POST /client/checkin</Text> and{' '}
						<Text strong>POST /client/checkout</Text> responses. Long messages stay
						unchanged in the check-in management service and are still logged on the
						server when Short is selected. Errors use a specific short line per situation
						when possible; the two “fallback” lines cover unknown or legacy errors.
					</Paragraph>

					<Title level={5}>Response style</Title>
					<Radio.Group
						value={style}
						onChange={(e) => setStyle(e.target.value)}
						style={{ marginBottom: 16 }}
					>
						<Space direction='vertical'>
							<Radio value='long'>
								<strong>Long message</strong> — full detail (current behaviour, best
								for debugging).
							</Radio>
							<Radio value='short'>
								<strong>Short message</strong> — user-friendly text below.
							</Radio>
						</Space>
					</Radio.Group>

					{style === 'short' ? (
						<>
							<Divider />
							<Title level={5}>Success messages</Title>
							<Row gutter={[16, 16]}>
								<Col xs={24} md={12}>
									<Text strong>Check-in success</Text>
									<Input
										style={{ marginTop: 8 }}
										value={shortInOk}
										maxLength={500}
										onChange={(e) => setShortInOk(e.target.value)}
									/>
								</Col>
								<Col xs={24} md={12}>
									<Text strong>Check-out success</Text>
									<Input
										style={{ marginTop: 8 }}
										value={shortOutOk}
										maxLength={500}
										onChange={(e) => setShortOutOk(e.target.value)}
									/>
								</Col>
							</Row>

							<Divider />
							<Title level={5}>Fallback errors</Title>
							<Paragraph type='secondary' style={{ marginTop: -8 }}>
								Used only when no situation-specific line applies (e.g. new error types).
							</Paragraph>
							<Row gutter={[16, 16]}>
								<Col xs={24} md={12}>
									<Text strong>Check-in fallback</Text>
									<Input
										style={{ marginTop: 8 }}
										value={shortInErr}
										maxLength={500}
										onChange={(e) => setShortInErr(e.target.value)}
									/>
								</Col>
								<Col xs={24} md={12}>
									<Text strong>Check-out fallback</Text>
									<Input
										style={{ marginTop: 8 }}
										value={shortOutErr}
										maxLength={500}
										onChange={(e) => setShortOutErr(e.target.value)}
									/>
								</Col>
							</Row>

							<Divider />
							<Collapse
								defaultActiveKey={['in', 'out']}
								items={[
									{
										key: 'in',
										label: 'Check-in situation messages (optional overrides)',
										children: (
											<Row gutter={[16, 24]}>
												{CHECKIN_REASON_ROWS.map(({ code, label }) =>
													reasonField(code, label, checkInReasons[code] ?? '', (v) =>
														setCheckInReasons((prev) => ({ ...prev, [code]: v })),
													),
												)}
											</Row>
										),
									},
									{
										key: 'out',
										label: 'Check-out situation messages (optional overrides)',
										children: (
											<Row gutter={[16, 24]}>
												{CHECKOUT_REASON_ROWS.map(({ code, label }) =>
													reasonField(code, label, checkOutReasons[code] ?? '', (v) =>
														setCheckOutReasons((prev) => ({ ...prev, [code]: v })),
													),
												)}
											</Row>
										),
									},
								]}
							/>
						</>
					) : null}

					<Space style={{ marginTop: 24 }}>
						<Button type='primary' onClick={handleSave} loading={saving}>
							Save
						</Button>
					</Space>
				</Card>
			</Spin>
		</div>
	)
}

export default MessageTypesTab
